import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('elev802_logout_reason');
      if (reason) {
        toast.message(reason);
        sessionStorage.removeItem('elev802_logout_reason');
      }
    } catch { /* ignore */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === 'coach') {
        navigate('/coach', { replace: true });
      } else if (!user.onboarding_complete) {
        navigate('/onboarding', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Login failed';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#F8FAFC] flex flex-col px-6 safe-top">
      <div className="pt-4">
        <button onClick={() => navigate('/')} className="text-[#64748B] hover:text-[#0A2242] transition-colors p-1" data-testid="back-to-landing">
          <ArrowLeft size={22} />
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full"
      >
        <div className="mb-8 flex flex-col items-center">
          <img src="/logo-coach-phone.png" alt="Elev802 Connected" className="h-24 w-auto mb-4 rounded-xl" />
          <h2 className="font-['Barlow_Condensed'] text-3xl font-bold tracking-tight text-[#0A2242]" data-testid="login-title">SIGN IN</h2>
          <p className="text-[#64748B] text-sm mt-2">Welcome back. Let's get to work.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-2 block">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent placeholder:text-[#94A3B8] transition-all"
              placeholder="you@email.com"
              required
              data-testid="login-email"
            />
          </div>
          <div className="relative">
            <label className="text-xs uppercase tracking-widest text-[#64748B] font-bold mb-2 block">Password</label>
            <input
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent placeholder:text-[#94A3B8] transition-all pr-12"
              placeholder="Enter password"
              required
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-[38px] text-[#64748B] hover:text-[#0A2242] transition-colors"
            >
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-colors disabled:opacity-50 mt-2"
            data-testid="login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          <p className="text-center mt-3">
            <Link to="/forgot-password" className="text-xs text-[#64748B] hover:text-[#2563EB] font-semibold transition-colors" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </p>
        </form>

        <p className="text-center text-sm text-[#64748B] mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#2563EB] hover:text-[#3B82F6] font-semibold">Sign Up</Link>
        </p>
      </motion.div>
    </div>
  );
}
